import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CommonModule, Location} from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

// SERVICES
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from 'ngx-toastr';
import { PayBillsService } from '../paybills.service';
import { PopUpDialogService } from 'src/app/services/popup-dialog.service';
import { ChangeDetectionService } from 'src/app/services/change-detection.service';
import { ImageValidatorHelperService } from 'src/app/services/image-validator-helper.service';

// TYPES
import { ImageFile } from 'src/app/core/model/common/ImageFile.interface';

// CONSTANTS
import { BILLER_CATEGORY_OPTIONS, FIELD_FORMAT_OPTIONS } from 'src/app/lib/constants/dropdown-options.constants';

// MODULES
import { SharedDirectivesModule } from "src/app/shared/modules/shared-directives.module";
import { DropdownFieldComponent } from 'src/app/shared/component/custom-dropdown/custom-dropdown.component';

@Component({
  selector: 'app-sku-paybills-edit',
  templateUrl: './sku-paybills-edit.component.html',
  styleUrls: ['./sku-paybills-edit.component.scss'],
  standalone: true,
  imports: [
    CommonModule, 
    ReactiveFormsModule, 
    SharedDirectivesModule,
    DropdownFieldComponent
  ],
})
export class SkuPaybillsEditComponent implements OnInit, OnDestroy {
  public updateForm: UntypedFormGroup;
  isLoading: boolean;
  billerName: any;
  uid: any;
  
  // Dropdown options
  billerCategoryOptions = BILLER_CATEGORY_OPTIONS;
  fieldFormatOptions = FIELD_FORMAT_OPTIONS;
  
  // Biller Category
  billerCategory_text: any;
  billerCategory: any;

  // First Field Format
  firstFieldFormat_text: any;
  firstFieldFormat: any;

  // Second Field Format
  secondFieldFormat_text: any;
  secondFieldFormat: any;

  // Image
  imageFile: ImageFile;
  existingImageUrl: string;
  
  // Store original values for change detection
  originalValues: any = {};
  
  // Cleanup subscription
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private changeDetectionService: ChangeDetectionService,
    private location: Location,
    public payBillsService: PayBillsService,
    public popUpDialog: PopUpDialogService,
    private imageValidatorService: ImageValidatorHelperService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.billerName = this.route.snapshot.paramMap.get('id');
    this.get_billers();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeForm() {
    this.updateForm = this.fb.group({
      billerName: [
        '', 
        [
          Validators.required,
          Validators.maxLength(50),
        ]
      ],
      billerDescription: [
        '', 
        [
          Validators.required,
          Validators.maxLength(50),
        ]
      ],
      firstFieldName: ['', Validators.required],
      secondFieldName: ['', Validators.required],
      firstFieldWidth: ['', Validators.required],
      secondFieldWidth: ['', Validators.required],
      convenienceFee: ['', Validators.required],
      serviceFeePercentage: [
        '', 
        [
          Validators.required,
          Validators.min(0),
          Validators.max(100),
        ],
      ],
      serviceFeeFixed: ['', Validators.required],
      image: [null]
    });

    // Subscribe to form changes to track modifications
    this.updateForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        console.log('Form values changed');
      });
  }

  async get_billers() {
    this.spinner.show();
    
    try {
      const response = await this.payBillsService.getBillerByName(this.billerName);
      
      if (response?.result?.data?.[0]) {
        const biller = response.result.data[0];
        this.uid = biller.uid;

        let percentage = biller.serviceFeePercentage;

        // Handle null serviceFeePercentage to prevent validation issues
        if (percentage === null || percentage === 0) {
          percentage = 0;
        } else if (percentage < 1) {
          percentage = percentage * 100;
        }
        
        percentage = parseFloat(percentage.toFixed(2));

        // Set form values
        this.updateForm.patchValue({
          billerName: biller.billerName,
          billerDescription: biller.billerDescription,
          firstFieldName: biller.firstFieldName,
          secondFieldName: biller.secondFieldName,
          firstFieldWidth: biller.firstFieldWidth,
          secondFieldWidth: biller.secondFieldWidth,
          convenienceFee: biller.convenienceFee,
          serviceFeePercentage: biller.serviceFeePercentage? Number((biller.serviceFeePercentage * 100).toFixed(2)) : 0,
          serviceFeeFixed: biller.serviceFeeFixed
        });

        // Set category
        this.billerCategory = biller.billerCategory;
        this.billerCategory_text = this.payBillsService.getCategoryText(biller.billerCategory);

        // Set field formats
        this.firstFieldFormat = biller.firstFieldFormat;
        this.firstFieldFormat_text = this.payBillsService.getFormatText(biller.firstFieldFormat);
        
        this.secondFieldFormat = biller.secondFieldFormat;
        this.secondFieldFormat_text = this.payBillsService.getFormatText(biller.secondFieldFormat);

        // Set existing image URL if available
        this.existingImageUrl = biller.imageUrl;
        
        // Store original values AFTER all data is loaded
        this.storeOriginalValues();
      }
    } catch (error) {
      this.toastr.error('Failed to load biller data', 'Error');
    } finally {
      setTimeout(() => {
        this.spinner.hide();
      }, 500);
    }
  }

  storeOriginalValues() {
    const valuesToStore = {
      billerName: this.updateForm.controls.billerName.value || '',
      billerDescription: this.updateForm.controls.billerDescription.value || '',
      firstFieldName: this.updateForm.controls.firstFieldName.value || '',
      secondFieldName: this.updateForm.controls.secondFieldName.value || '',
      firstFieldWidth: this.updateForm.controls.firstFieldWidth.value || '',
      secondFieldWidth: this.updateForm.controls.secondFieldWidth.value || '',
      convenienceFee: this.updateForm.controls.convenienceFee.value || '',
      serviceFeePercentage: this.updateForm.controls.serviceFeePercentage.value || '',
      serviceFeeFixed: this.updateForm.controls.serviceFeeFixed.value || '',
      billerCategory: this.billerCategory || '',
      firstFieldFormat: this.firstFieldFormat || '',
      secondFieldFormat: this.secondFieldFormat || '',
      existingImageUrl: this.existingImageUrl || '',
      hasNewImage: false
    };

    this.originalValues = this.changeDetectionService.cloneValues(valuesToStore);
  }

  getCurrentValues(): any {
    return {
      billerName: this.updateForm.controls.billerName.value || '',
      billerDescription: this.updateForm.controls.billerDescription.value || '',
      firstFieldName: this.updateForm.controls.firstFieldName.value || '',
      secondFieldName: this.updateForm.controls.secondFieldName.value || '',
      firstFieldWidth: this.updateForm.controls.firstFieldWidth.value || '',
      secondFieldWidth: this.updateForm.controls.secondFieldWidth.value || '',
      convenienceFee: this.updateForm.controls.convenienceFee.value || '',
      serviceFeePercentage: this.updateForm.controls.serviceFeePercentage.value || '',
      serviceFeeFixed: this.updateForm.controls.serviceFeeFixed.value || '',
      billerCategory: Number(this.billerCategory || ''),
      firstFieldFormat: Number(this.firstFieldFormat || ''),
      secondFieldFormat: Number(this.secondFieldFormat || ''),
      existingImageUrl: this.existingImageUrl || '',
      hasNewImage: !!this.imageFile?.file
    };
  }

  hasChanges(): boolean {
    const currentValues = this.getCurrentValues();
    const hasFormChanges = this.changeDetectionService.hasChanges(
      this.originalValues, 
      currentValues
    );
    
    return hasFormChanges;
  }

  isSubmitDisabled(): boolean {
    if (!this.originalValues || Object.keys(this.originalValues).length === 0) {
      return true;
    }
    
    return !this.hasChanges() || 
      !this.billerCategory || !this.firstFieldFormat || !this.secondFieldFormat;
  }

  cancel(): void {
    if (!this.hasChanges()) {
      this.location.back();
      return;
    }
    
    this.popUpDialog.openInfoActionDialog(
      'You have unsaved changes. Are you sure you want to cancel?',
      () => {
        this.location.back();
        this.toastr.success('Changes discarded', 'Success');
      },
      () => {
        console.log('Cancel edit cancelled by user');
      }
    );
  }

  async update() {
    this.updateForm.markAllAsTouched();

    const billerNameControl = this.updateForm.controls.billerName;
    if (!billerNameControl.value) {
      this.toastr.error('Biller Name is required!', 'Validation Error');
      return;
    }
    if (billerNameControl.hasError('maxlength')) {
      this.toastr.error('Biller Name must not exceed 50 characters!', 'Validation Error');
      return;
    }
    if (billerNameControl.hasError('pattern')) {
      this.toastr.error('Biller Name must contain only letters and spaces!', 'Validation Error');
      return;
    }

    const serviceFeeCtrl = this.updateForm.controls.serviceFeePercentage;
    if (serviceFeeCtrl.hasError('min') || serviceFeeCtrl.hasError('max')) {
      this.toastr.error('Service Fee Percentage must be between 0 and 100', 'Validation Error');
      return;
    }

    const billerDescControl = this.updateForm.controls.billerDescription;
    if (!billerDescControl.value) {
      this.toastr.error('Biller Description is required!', 'Validation Error');
      return;
    }
    if (billerDescControl.hasError('maxlength')) {
      this.toastr.error('Biller Description must not exceed 50 characters!', 'Validation Error');
      return;
    }
    if (billerDescControl.hasError('pattern')) {
      this.toastr.error('Biller Description must contain only letters, spaces, and only "&" for special characters!', 'Validation Error');
      return;
    }

    if (!this.updateForm.controls.firstFieldName.value) {
      this.toastr.error('First Field Name is required!', 'Validation Error');
      return;
    }

    if (!this.updateForm.controls.firstFieldWidth.value) {
      this.toastr.error('First Field Max Length is required!', 'Validation Error');
      return;
    }

    if (!this.updateForm.controls.secondFieldName.value) {
      this.toastr.error('Second Field Name is required!', 'Validation Error');
      return;
    }

    if (!this.updateForm.controls.secondFieldWidth.value) {
      this.toastr.error('Second Field Max Length is required!', 'Validation Error');
      return;
    }

    const convenienceFee = this.updateForm.controls.convenienceFee.value;
    if (convenienceFee === null || convenienceFee === undefined || convenienceFee === '') {
      this.toastr.error('Convenience Fee is required!', 'Validation Error');
      return;
    }

    const serviceFeeFixedValue = this.updateForm.controls.serviceFeeFixed.value;
    if (serviceFeeFixedValue === null || serviceFeeFixedValue === undefined || serviceFeeFixedValue === '') {
      this.toastr.error('Service Fee Fixed is required!', 'Validation Error');
      return;
    }

    const serviceFeePercentageValue = this.updateForm.controls.serviceFeePercentage.value;
    if (serviceFeePercentageValue === null || serviceFeePercentageValue === undefined || serviceFeePercentageValue === '') {
      this.toastr.error('Service Fee Percentage is required!', 'Validation Error');
      return;
    }

    if (!this.billerCategory) {
      this.toastr.error('Biller Category is required!', 'Validation Error');
      return;
    }

    if (!this.firstFieldFormat) {
      this.toastr.error('First Field Format is required!', 'Validation Error');
      return;
    }

    if (!this.secondFieldFormat) {
      this.toastr.error('Second Field Format is required!', 'Validation Error');
      return;
    }

    if (!this.hasChanges()) {
      this.toastr.info('No changes detected', 'Info');
      return;
    }

    let rawPercentage = Number(this.updateForm.controls.serviceFeePercentage.value);

    let finalPercentage = 0;

    if (!rawPercentage) {
      finalPercentage = 0;
    } else if (rawPercentage >= 1) {
      // Treat as percent (1 → 1%)
      finalPercentage = Number((rawPercentage / 100).toFixed(6));
    } else {
      // Treat as decimal (0.01 → 1%)
      finalPercentage = Number(rawPercentage.toFixed(6));
    }


    const formData = {
      billerName: this.updateForm.controls.billerName.value,
      billerDescription: this.updateForm.controls.billerDescription.value,
      firstFieldName: this.updateForm.controls.firstFieldName.value,
      firstFieldWidth: this.updateForm.controls.firstFieldWidth.value,
      secondFieldName: this.updateForm.controls.secondFieldName.value,
      secondFieldWidth: this.updateForm.controls.secondFieldWidth.value,
      convenienceFee: this.updateForm.controls.convenienceFee.value,
      serviceFeePercentage: finalPercentage,
      serviceFeeFixed: this.updateForm.controls.serviceFeeFixed.value,
      billerCategory: this.billerCategory,
      firstFieldFormat: this.firstFieldFormat,
      secondFieldFormat: this.secondFieldFormat
    };

    const response = await this.payBillsService.performUpdate(this.uid, formData, this.imageFile);

    if (response.success) {
      this.imageFile = null;
      this.router.navigate(['features/sku-pay-bills']);
    }
  }

  setBillerCategory(label: string, value: string) {
    this.billerCategory = value;
    this.billerCategory_text = label;
  }

  setFirstFieldFormat(label: string, value: string) {
    this.firstFieldFormat = value;
    this.firstFieldFormat_text = label;
  }

  setSecondFieldFormat(label: string, value: string) {
    this.secondFieldFormat = value;
    this.secondFieldFormat_text = label;
  }

  viewUploadedImage() {
    if (this.imageFile && this.imageFile.link) {
      this.popUpDialog.openImageViewDialog(this.imageFile.link, this.imageFile.name);
    }
  }

  openSite(imageUrl: string) {
    window.open(imageUrl, '_blank');
  }

  async onSelectFile(event) {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const previousImageFile = this.imageFile ? { ...this.imageFile } : null;
      
      const imageFile = await this.imageValidatorService.processFileSelection(file, this.payBillsService.maxFileSize, this.payBillsService.maxImageDimension);
      
      if (imageFile) {
        this.imageFile = imageFile;
        this.updateForm.get('image')?.markAsTouched();
        this.updateForm.get('image')?.updateValueAndValidity();
        console.log('New image selected:', file.name);
      } else {
        if (previousImageFile) {
          this.imageFile = previousImageFile;
        } else {
          this.imageFile = null;
        }
        event.target.value = '';
      }
    }
  }
}
